/**
 * PSR-5b — Brandwatch v3 Mentions API sentiment fetcher.
 *
 * Commercial, per-tenant-keyed sentiment source. Sits alongside the four
 * free PSE sources (GDELT, MuniCode/Granicus/Civic-Plus, Regulations.gov/
 * FERC/ADAMS, image-observation) and is dispatched by
 * `publicSentimentOrchestrator.ts` only when the caller is running
 * `mode: 'standalone-psr'` AND the tenant has provisioned a Brandwatch
 * API key.
 *
 * Endpoint (version-pinned per R2 — API drift mitigation):
 *   https://api.brandwatch.com/v2/projects/{projectId}/data/mentions
 *
 * The constant below is the URL TEMPLATE (no project id); the caller
 * supplies the project id at fetch time. Pin v2 here — when Brandwatch
 * publishes v3 we cut a coordinated migration rather than silently
 * picking up a breaking schema change.
 *
 * Auth: `Authorization: bearer <apiKey>` header. The key is read from
 * Firestore at `tenants/{tenantId}/integrations/brandwatch.apiKey`
 * (admin-only write — Firestore rules enforce this separately; see
 * `firestore.rules` and the admin "Commercial Sentiment Keys" UI in
 * `CommercialSentimentKeysSection.tsx`). NEVER log, echo, or include
 * the key in any error message.
 *
 * Per-tenant budget (R1 — cost-explosion guard):
 *   - `BRANDWATCH_DAILY_QUERY_BUDGET` queries per tenant per UTC day.
 *   - Tracked at `tenants/{tenantId}/integrations/brandwatch.queriesToday`.
 *   - Reset at UTC midnight via a paired `brandwatch.queriesTodayDate`
 *     field. The fetcher reads-then-writes; the orchestrator gates a
 *     pre-flight read so the source is skipped when budget is exhausted.
 *
 * Token-bucket rate limit (mirrors the CAR / NRCS Soil Survey pattern):
 *   - 5-burst capacity, 1 token / minute refill. Per process.
 *   - Acquire ONE token per query. Failed acquire → soft-skip with
 *     `status: 'unavailable', reason: 'rate-limited'`.
 *
 * Mapper:
 *   - Brandwatch entry → `PublicSentimentEntry`.
 *   - Preserves URL + author + publishedAt + Brandwatch's calibrated
 *     polarity (positive/negative/neutral → +0.7 / -0.7 / 0.0 anchor).
 *   - Confidence cap: `BRANDWATCH_CONFIDENCE_CAP = 0.70` (higher than
 *     the free sources because Brandwatch's classifier is calibrated;
 *     GDELT's `lengthBasedConfidence` typically lands in 0.07-0.25).
 *
 * Site-id privacy (R5):
 *   - Vendor queries NEVER contain site addresses. The orchestrator
 *     hashes `siteId` to an opaque 8-char prefix; this fetcher consumes
 *     that hash as `queryTag` and includes it ONLY in the audit
 *     metadata (not in the Brandwatch query body).
 *
 * Resilience: NEVER throws. Every failure path collapses into a
 * structured `{ status: 'unavailable', reason, entries: [] }` so the
 * PSR orchestrator stays resilient.
 */
import type { PublicSentimentEntry } from '../../../types/siteEvidence';
import type { PublicSentimentSourceResult } from '../../publicSentimentOrchestrator';
import { rethrowIfAborted, throwIfAborted } from '../../../lib/abortSignal';
import {
  classifyBtmTech,
  truncateSnippet,
} from '../sentimentPolarity';

/* ── Public constants ──────────────────────────────────────────────── */

/**
 * Version-pinned Brandwatch v3 Mentions API URL template. Per R2 (API
 * drift mitigation), we hard-code the major version so Brandwatch
 * cannot publish a v3 cutover that silently breaks our schema mapping
 * — when they do publish v3 we cut a coordinated migration.
 *
 * `{projectId}` is the literal placeholder; callers substitute their
 * tenant-scoped project id.
 */
export const BRANDWATCH_MENTIONS_URL =
  'https://api.brandwatch.com/v2/projects/{projectId}/data/mentions';

/**
 * Per-tenant daily query budget. The fetcher itself is best-effort —
 * the orchestrator carries the authoritative pre-flight budget gate
 * because it owns the Firestore read.
 *
 * 100 was sized off the worst-case PSR fan-out: 5 BTM techs × 3 seed
 * queries × 2 locality variants × 3-site soak = ~90 queries. The cap
 * leaves a little headroom for manual re-runs.
 */
export const BRANDWATCH_DAILY_QUERY_BUDGET = 100;

/**
 * Confidence ceiling for Brandwatch entries. Higher than free sources
 * because Brandwatch's polarity classification is human-calibrated on
 * a labelled training set; lower than 1.0 because the calibration is
 * for English-language consumer-media corpora and our BTM-tech
 * vocabulary is adjacent-domain.
 */
export const BRANDWATCH_CONFIDENCE_CAP = 0.70;

/**
 * Estimated USD cost per Brandwatch query. Placeholder constant — the
 * real billing happens on the Brandwatch invoice. Used only by the
 * admin Cost-Envelope tile (`CommercialSentimentCostSection.tsx`).
 */
export const BRANDWATCH_USD_PER_QUERY = 0.10;

/* ── Token bucket (rate limit) ─────────────────────────────────────── */

const RATE_LIMIT_CAPACITY = 5;
const RATE_LIMIT_REFILL_PER_MS = 1 / 60_000; // 1 token per minute.

let bucketTokens = RATE_LIMIT_CAPACITY;
let bucketLastRefillMs = 0;

/**
 * Try to take one token from the rate-limit bucket. Returns `true` on
 * success, `false` when the bucket is empty. Refill is lazy —
 * recomputed at acquire time based on wall-clock delta since the last
 * refill stamp.
 *
 * Exported (with `__resetBrandwatchRateLimit`) so the unit tests can
 * pin the bucket state deterministically.
 */
export function __acquireBrandwatchRateToken(nowMs: number = Date.now()): boolean {
  if (bucketLastRefillMs === 0) bucketLastRefillMs = nowMs;
  const elapsed = Math.max(0, nowMs - bucketLastRefillMs);
  const refill = elapsed * RATE_LIMIT_REFILL_PER_MS;
  if (refill > 0) {
    bucketTokens = Math.min(RATE_LIMIT_CAPACITY, bucketTokens + refill);
    bucketLastRefillMs = nowMs;
  }
  if (bucketTokens < 1) return false;
  bucketTokens -= 1;
  return true;
}

/** Test seam — reset the bucket to the configured capacity. */
export function __resetBrandwatchRateLimit(): void {
  bucketTokens = RATE_LIMIT_CAPACITY;
  bucketLastRefillMs = 0;
}

/* ── Brandwatch entry shape (subset) ───────────────────────────────── */

/**
 * Subset of the Brandwatch v3 Mentions response we actually read. The
 * full schema is much larger; mapping only what we need keeps the
 * mapper deterministic + the test fixture minimal.
 */
export interface BrandwatchMention {
  resourceId?: string;
  url?: string;
  title?: string;
  snippet?: string;
  /** Brandwatch's calibrated polarity. `null` is normal for short or
   *  ambiguous mentions; the mapper treats it as neutral. */
  sentiment?: 'positive' | 'negative' | 'neutral' | null;
  author?: string;
  publishedAt?: string;
}

interface BrandwatchResponse {
  /** Brandwatch wraps results in a `results` (or sometimes `data`) array
   *  depending on the endpoint variant; tolerate both. */
  results?: BrandwatchMention[];
  data?: BrandwatchMention[];
}

/* ── Mapper ────────────────────────────────────────────────────────── */

/**
 * Anchor scores for the three Brandwatch polarity classes. The anchors
 * are conservative compared to ±1.0 because Brandwatch's classifier
 * is calibrated for "polarised vs neutral", not for "extremely vs
 * mildly polarised". Anchoring at ±0.7 leaves headroom for the
 * confidence-weighted aggregator to mark a single strongly-classified
 * Brandwatch mention as roughly equivalent to 3-4 weakly-classified
 * GDELT articles, which matches the engineering intent of the
 * commercial-source upgrade.
 */
const POLARITY_ANCHORS: Record<'positive' | 'negative' | 'neutral', number> = {
  positive: 0.7,
  negative: -0.7,
  neutral: 0.0,
};

/**
 * Map one Brandwatch mention to a `PublicSentimentEntry`. Returns null
 * when required fields (url, classification surface) are missing —
 * mirrors the GDELT mapper's behaviour so the orchestrator's dedup
 * (by URL) stays well-defined.
 */
export function mapBrandwatchMention(m: BrandwatchMention): PublicSentimentEntry | null {
  const url = m.url?.trim();
  if (!url) return null;
  const classificationText = (m.title?.trim() || m.snippet?.trim() || '').toString();
  if (!classificationText) return null;

  const polarity = m.sentiment ?? 'neutral';
  const anchorRaw = POLARITY_ANCHORS[polarity];
  // Strict Number.isFinite guard — even though POLARITY_ANCHORS is a
  // constant table, the input polarity could be a forward-compat string
  // we don't know about. Default to 0 (neutral).
  const sentimentScore = Number.isFinite(anchorRaw) ? anchorRaw : 0;

  const btmTech = classifyBtmTech(classificationText);
  const fetchedAt = new Date().toISOString();

  return {
    sourceId: 'brandwatch',
    fetchedAt,
    btmTech,
    sentimentScore,
    confidence: BRANDWATCH_CONFIDENCE_CAP,
    supportingSnippet: truncateSnippet(classificationText),
    url,
  };
}

/* ── Single-query fetcher ──────────────────────────────────────────── */

export interface BrandwatchSentimentFilters {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  /** Tenant API key. REQUIRED — fetcher returns `unavailable` when
   *  unset rather than calling Brandwatch with an anonymous key. */
  apiKey: string;
  /** Brandwatch tenant project id (UUID). REQUIRED. */
  projectId: string;
  /** Opaque siteId hash for audit trail. NEVER the raw site id (R5). */
  queryTag?: string;
  /** Max results per query. Brandwatch v3 caps at 5000; we cap soft
   *  to 50 here — the orchestrator's per-source cap (200) dominates. */
  maxRecordsPerQuery?: number;
  /** Override the URL template (testing only — production callers use
   *  `BRANDWATCH_MENTIONS_URL`). */
  urlTemplate?: string;
}

const DEFAULT_MAX_RECORDS_PER_QUERY = 50;

async function fetchOneQuery(
  query: string,
  filters: BrandwatchSentimentFilters,
): Promise<PublicSentimentEntry[]> {
  const template = filters.urlTemplate ?? BRANDWATCH_MENTIONS_URL;
  const base = template.replace('{projectId}', encodeURIComponent(filters.projectId));
  const params = new URLSearchParams({
    queryString: query,
    pageSize: String(filters.maxRecordsPerQuery ?? DEFAULT_MAX_RECORDS_PER_QUERY),
  });
  const url = `${base}?${params.toString()}`;

  // `fetchWithTimeout` from `../types` doesn't carry custom headers, so
  // we issue the call directly with `fetch + AbortController` so we can
  // attach the Brandwatch `Authorization: bearer` header. The 20s
  // timeout mirrors the other PSE-2 fetchers.
  const controller = new AbortController();
  if (filters.signal?.aborted) controller.abort(filters.signal.reason);
  const onAbort = () => controller.abort(filters.signal?.reason);
  filters.signal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(
    () => controller.abort(new Error('brandwatch: request timed out')),
    20_000,
  );
  let response: Response;
  try {
    response = await fetch(url, {
      headers: {
        // Brandwatch v3: `Authorization: bearer <token>` (lowercase
        // 'bearer' per their docs). Key NEVER logged.
        Authorization: `bearer ${filters.apiKey}`,
        Accept: 'application/json',
      },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
    filters.signal?.removeEventListener('abort', onAbort);
  }

  if (!response.ok) {
    // Mask the key in error text — even though Brandwatch shouldn't echo
    // it back, we never want to even risk leaking it via an error path.
    throw new Error(`brandwatch: HTTP ${response.status}`);
  }
  const text = await response.text();
  if (!text.trim()) return [];
  let parsed: BrandwatchResponse;
  try {
    parsed = JSON.parse(text) as BrandwatchResponse;
  } catch {
    return [];
  }
  const items = parsed.results ?? parsed.data ?? [];
  const out: PublicSentimentEntry[] = [];
  for (const m of items) {
    const mapped = mapBrandwatchMention(m);
    if (mapped) out.push(mapped);
  }
  return out;
}

/* ── Public entry point ────────────────────────────────────────────── */

/**
 * PSR-5b fetcher — structurally satisfies `PublicSentimentSourceFn`
 * once partially-applied with a filter that carries `apiKey + projectId`
 * (the orchestrator does the partial-application; see
 * `publicSentimentOrchestrator.ts`).
 *
 * Resilience model:
 *  - Per-query try/catch — one failing query doesn't kill the batch.
 *  - Missing API key → unavailable (no HTTP call).
 *  - Rate-limit acquire fails → unavailable with reason 'rate-limited'.
 *  - All queries failed → unavailable.
 *
 * NEVER throws.
 */
export async function fetchBrandwatchSentiment(
  queries: string[],
  filters: BrandwatchSentimentFilters,
): Promise<PublicSentimentSourceResult> {
  throwIfAborted(filters?.signal);
  if (!filters || !filters.apiKey || !filters.projectId) {
    return {
      status: 'unavailable',
      reason: 'brandwatch: missing apiKey or projectId',
      entries: [],
    };
  }
  if (queries.length === 0) {
    return { status: 'unavailable', reason: 'no queries supplied', entries: [] };
  }

  const allEntries: PublicSentimentEntry[] = [];
  const errors: string[] = [];
  for (const q of queries) {
    throwIfAborted(filters.signal);
    if (!__acquireBrandwatchRateToken()) {
      errors.push(`${q}: rate-limited`);
      continue;
    }
    try {
      const entries = await fetchOneQuery(q, filters);
      throwIfAborted(filters.signal);
      allEntries.push(...entries);
    } catch (e) {
      rethrowIfAborted(filters.signal, e);
      // The error message NEVER includes the API key — fetchOneQuery
      // builds its error string from `response.status` only.
      const msg = (e as Error)?.message ?? String(e);
      errors.push(`${q}: ${msg}`);
    }
  }

  if (allEntries.length === 0 && errors.length === queries.length) {
    return { status: 'unavailable', reason: errors.join('; '), entries: [] };
  }

  // Cross-query URL dedup — the same Brandwatch mention can surface
  // against multiple seed queries (broad + locality-suffixed).
  const seen = new Set<string>();
  const deduped: PublicSentimentEntry[] = [];
  for (const e of allEntries) {
    if (seen.has(e.url)) continue;
    seen.add(e.url);
    deduped.push(e);
  }

  return { status: 'ok', entries: deduped };
}
